import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, forkJoin } from 'rxjs';
import { GlobalService } from 'src/app/_services/global.service';
import { BusBulkEnrollService } from 'src/app/buses/_services/bus-bulk-enroll.service';
import { AutoUnsubscribe } from 'src/app/shared/decorators/auto-unsubscribe.decorator';
import { ErrorMsgService } from 'src/app/shared/error-msg/error-msg.service';
declare var $: any;

@AutoUnsubscribe
@Component({
  selector: 'app-assign-trips',
  templateUrl: './assign-trips.component.html',
  styleUrls: ['./assign-trips.component.scss']
})
export class AssignTripsComponent {
  destroy$ = new Subject<void>();
  currLang

  layoutDirection: 'ltr' | 'rtl' = 'ltr'; // Dynamically switchable

  // Trip-level color memory
  private tripStyleMap = new Map<string, { bg: string; text: string }>();
  subscribers: any[] = [];
  trips: any[] = [];

  // Add property to track select all state
  selectAll: boolean = false;

  // Example trips data
  selectedTripType: 'morning' | 'afternoon' = 'morning';
  selectedTrip: any | null = null;

  dropdownOpen: boolean = false;
  current_upload_batch_id = null;

  // Search and filter properties
  searchTerm: string = '';
  filterType: 'all' | 'with_trips' | 'without_trips' = 'all';

  // Initial counts (set when data is loaded)
  private initialEnrolledCount: number = 0;
  private initialWithTripsCount: number = 0;
  private initialWithoutTripsCount: number = 0;

  private lastLoadedBatchId: any = null;
  loading = false;

  showConfirmModal = false;
  showNavigationModal = false;
  navigationRoute: string = '/buses/students';

  allowNavigation = false;
  resolveNavigation: ((result: boolean) => void) | null = null;


  constructor(
    private globalService: GlobalService,
    private translate: TranslateService,
    private errorMsgService: ErrorMsgService,
    private router: Router,
    private busBulkEnrollService: BusBulkEnrollService

  ) {

  }
  ngOnInit(): void {
    $('.modal').modal('hide');
    $('body').removeClass('modal-open');
    $('.modal-backdrop').remove();
    this.globalService.getLang().pipe(takeUntil(this.destroy$)).subscribe(lng => {
      this.currLang = lng;
    });
    this.globalService.getBulkBusEnroll().pipe(takeUntil(this.destroy$)).subscribe(res => {
      res?.batch_id ? this.setCurrentUploadBatchId(res?.batch_id) : this.setCurrentUploadBatchId(localStorage.getItem('bulkBusEnrollBatchId'));
      this.loadData();
    });
  }

  loadData() {
    if (!this.current_upload_batch_id) return;
    this.loading = true;
    forkJoin({
      subscribers: this.busBulkEnrollService.getEnrolledStudents({}, {}, { batch_id: this.current_upload_batch_id }),
      trips: this.busBulkEnrollService.getAvailableTripsFromBus({}, {}, { batch_id: this.current_upload_batch_id })
    }).pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
      const subscribers = result.subscribers;
      const trips = result.trips;
      this.subscribers = (subscribers?.data || []).map((sub: any) => ({
        ...sub,
        selected: false,
        trips: (sub.trips || []).map(String)
      }));
      this.trips = (trips?.data || []).map((trip: any) => ({
        ...trip,
        trip_id: String(trip.trip_id),
        subscribers: []
      }));

      // --- Ensure mapping between subscriber.trips and trip.subscribers ---
      this.subscribers.forEach(sub => {
        if (Array.isArray(sub.trips)) {
          sub.trips.forEach(tripId => {
            const trip = this.getTripById(tripId);
            if (trip && !trip.subscribers.includes(sub.id)) {
              trip.subscribers.push(sub.id);
            }
          });
        }
      });
      // --- End mapping ---

      // Calculate and store initial counts
      this.calculateInitialCounts();
      this.loading = false;
    }, (err: any) => {
      this.loading = false;
      this.errorMsgService.setMessage(err?.error?.message);
    });
  }

  private calculateInitialCounts() {
    this.initialEnrolledCount = this.subscribers.length;
    this.initialWithTripsCount = this.subscribers.filter(sub => sub.trips && sub.trips.length > 0).length;
    this.initialWithoutTripsCount = this.subscribers.filter(sub => !sub.trips || sub.trips.length === 0).length;
  }

  getTripById(tripId: string) {
    return this.trips.find(t => t.trip_id == tripId);
  }

  getAssignedSubscribers(tripId: string) {
    return this.subscribers.filter(sub => sub.trips.map(String).includes(String(tripId)));
  }

  getTagColor(tripName: string): string {
    let hash = 0;
    for (let i = 0; i < tripName.length; i++) {
      hash = tripName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    const saturation = 60 + (hash % 20);
    const lightness = 80;
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }

  getAdaptiveTextColor(tripName: string): string {
    let hash = 0;
    for (let i = 0; i < tripName.length; i++) {
      hash = tripName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    const saturation = 60 + (hash % 20);
    const lightness = 30;
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }

  getTripStyle(subscriber: any, tripName: string, index: number) {
    const uniqueKey = `${subscriber.id}-${tripName}-${index}`;
    if (!this.tripStyleMap.has(uniqueKey)) {
      const hue = Math.floor(Math.random() * 360);
      const saturation = 60 + Math.random() * 20;
      const lightness = 85;
      const bg = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
      const text = `hsl(${hue}, ${saturation}%, 25%)`;
      this.tripStyleMap.set(uniqueKey, { bg, text });
    }
    return this.tripStyleMap.get(uniqueKey)!;
  }

  // Assign a trip to a subscriber
  assignTripToSubscriber(subscriber: any, trip: any) {
    if (!subscriber.trips.includes(trip.trip_id)) {
      subscriber.trips.push(trip.trip_id);
    }
    // Add subscriber to trip's subscribers array
    if (!trip.subscribers.includes(subscriber.id)) {
      trip.subscribers.push(subscriber.id);
    }
    this.syncSelectedState();
  }

  // Remove a trip from a subscriber
  removeTripFromSubscriber(subscriber: any, trip: any) {
    const idx = subscriber.trips.indexOf(trip.trip_id);
    if (idx !== -1) {
      subscriber.trips.splice(idx, 1);
    }
    // Remove subscriber from trip's subscribers array
    const subscriberIdx = trip.subscribers.indexOf(subscriber.id);
    if (subscriberIdx !== -1) {
      trip.subscribers.splice(subscriberIdx, 1);
    }
    this.syncSelectedState();
  }

  // Remove trip from tag click
  removeTripTag(subscriber: any, tripId: string, event: any) {
    event.stopPropagation();
    event.preventDefault();
    const trip = this.getTripById(tripId);
    if (trip) {
      subscriber.trips = subscriber.trips.filter(tripItem => String(tripItem) !== String(tripId));
      // Remove subscriber from trip's subscribers array
      trip.subscribers = trip.subscribers.filter(subId => subId !== subscriber.id);
      if (this.selectedTrip && this.selectedTrip.trip_id === tripId) {
        subscriber.selected = false;
        this.updateSelectAllState();
      }
      this.syncSelectedState();
    }
  }

  // Select all/deselect all logic
  toggleSelectAll() {
    this.selectAll = !this.selectAll;
    if (!this.selectedTrip) return;

    // Get the currently filtered subscribers
    const currentFilteredSubscribers = this.filteredSubscribers;

    // Only operate on the filtered subscribers
    currentFilteredSubscribers.forEach(sub => {
      sub.selected = this.selectAll;
      const hasTrip = sub.trips.includes(this.selectedTrip.trip_id);
      if (this.selectAll && !hasTrip) {
        sub.trips.push(this.selectedTrip.trip_id);
        // Add subscriber to trip's subscribers array
        if (!this.selectedTrip.subscribers.includes(sub.id)) {
          this.selectedTrip.subscribers.push(sub.id);
        }
      } else if (!this.selectAll && hasTrip) {
        this.removeTripFromSubscriber(sub, this.selectedTrip);
      }
    });
    this.syncSelectedState();
  }

  onSubscriberSelectionChange() {
    if (!this.selectedTrip) return;
    this.filteredSubscribers.forEach(sub => {
      const hasTrip = sub.trips.includes(this.selectedTrip.trip_id);
      if (sub.selected && !hasTrip) {
        sub.trips.push(this.selectedTrip.trip_id);
        if (!this.selectedTrip.subscribers.includes(sub.id)) {
          this.selectedTrip.subscribers.push(sub.id);
        }
      } else if (!sub.selected && hasTrip) {
        sub.trips = sub.trips.filter(tid => tid !== this.selectedTrip.trip_id);
        this.selectedTrip.subscribers = this.selectedTrip.subscribers.filter(sid => sid !== sub.id);
      }
    });
    this.syncSelectedState();
  }

  toggleTripType(type: 'morning' | 'afternoon') {
    this.selectedTripType = type;
    this.selectedTrip = null;
    this.clearSubscriberSelections();
  }

  selectTrip(trip: any) {
    this.selectedTrip = trip;
    if (this.selectedTrip) {
      this.subscribers.forEach(sub => {
        sub.selected = sub.trips.includes(this.selectedTrip.trip_id);
      });
      this.updateSelectAllState();
    } else {
      this.clearSubscriberSelections();
    }
  }

  // Utility to sync selected state for all subscribers based on selectedTrip
  syncSelectedState() {
    if (this.selectedTrip) {
      this.subscribers.forEach(sub => {
        sub.selected = sub.trips.includes(this.selectedTrip.trip_id);
      });
      this.updateSelectAllState();
    }
  }

  clearSubscriberSelections() {
    // Get the currently filtered subscribers
    const currentFilteredSubscribers = this.filteredSubscribers;

    // Only clear selections for filtered subscribers
    currentFilteredSubscribers.forEach(sub => sub.selected = false);
    this.selectAll = false;
  }

  removeSubscriberFromTrip(subscriber: any, trip: any) {
    this.removeTripFromSubscriber(subscriber, trip);
    if (this.selectedTrip && this.selectedTrip.trip_id === trip.trip_id) {
      subscriber.selected = false;
      this.updateSelectAllState();
    }
    this.syncSelectedState();
  }

  removeSubscriberFromSelectedTrip(subscriber: any) {
    if (!this.selectedTrip) return;
    this.removeSubscriberFromTrip(subscriber, this.selectedTrip);
    this.syncSelectedState();
  }

  removeSubscriberFromAllTrips(subscriber: any) {
    // Remove subscriber from all trips' subscribers arrays
    subscriber.trips.forEach(tripId => {
      const trip = this.getTripById(tripId);
      if (trip) {
        trip.subscribers = trip.subscribers.filter(subId => subId !== subscriber.id);
      }
    });
    subscriber.trips = [];
    subscriber.selected = false;
    this.updateSelectAllState();
    this.syncSelectedState();
  }

  clearAllAssignments() {
    // Clear all trips' subscribers arrays
    this.trips.forEach(trip => {
      trip.subscribers = [];
    });
    this.subscribers.forEach(sub => {
      sub.trips = [];
      sub.selected = false;
    });
    this.selectAll = false;
    this.syncSelectedState();
  }

  updateSelectAllState() {
    if (!this.selectedTrip) {
      this.selectAll = false;
      return;
    }

    // Get the currently filtered subscribers
    const currentFilteredSubscribers = this.filteredSubscribers;

    // Check if all filtered subscribers are selected
    const allSelected = currentFilteredSubscribers.length > 0 && currentFilteredSubscribers.every(sub => sub.selected);
    this.selectAll = allSelected;
  }

  get filteredTrips() {
    return this.trips.filter(trip => trip.type === this.selectedTripType);
  }

  onTripDropdownChange(event: any) {
    const selectedTrip = event.target.value ? this.filteredTrips.find(trip => trip.trip_id == event.target.value) : null;
    this.selectTrip(selectedTrip);
  }

  getSubscriberName(id: number): string {
    const sub = this.subscribers.find(s => s.id === id);
    return sub ? sub.name : '';
  }

  getSubscriberById(id: number): any {
    return this.subscribers.find(s => s.id === id);
  }

  setCurrentUploadBatchId(id: any) {
    this.current_upload_batch_id = id ? id : null;
  }

  ngOnDestroy(): void {
    this.errorMsgService.setMessage('');
  }

  // Search and filter methods
  onSearchChange(event: any) {
    this.searchTerm = event.target.value;
    // Reset select all state when search changes
    this.updateSelectAllState();
  }

  onFilterChange(event: any) {
    this.filterType = event.target.value;
    // Reset select all state when filter changes
    this.updateSelectAllState();
  }

  // Computed property for filtered subscribers
  get filteredSubscribers() {
    let filtered = this.subscribers;

    // Apply search filter
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      filtered = filtered.filter(sub =>
        sub.name.toLowerCase().includes(searchLower) ||
        sub.national_id.toLowerCase().includes(searchLower)
      );
    }

    // Apply trip assignment filter
    switch (this.filterType) {
      case 'with_trips':
        filtered = filtered.filter(sub => sub.trips && sub.trips.length > 0);
        break;
      case 'without_trips':
        filtered = filtered.filter(sub => !sub.trips || sub.trips.length === 0);
        break;
      case 'all':
      default:
        // No additional filtering needed
        break;
    }

    return filtered;
  }

  // Get metrics for the cards (return initial counts)
  get enrolledCount() {
    return this.initialEnrolledCount;
  }

  get withTripsCount() {
    return this.initialWithTripsCount;
  }

  get withoutTripsCount() {
    return this.initialWithoutTripsCount;
  }
  save() {
    const hasSubscriberWithoutTrips = this.subscribers.some(sub => !sub?.trips || sub?.trips?.length === 0);
    if (hasSubscriberWithoutTrips) {
      this.showNavigationModal = false;
      this.showConfirmModal = true;
    } else {
      this.saveAssignments();
    }
  }

  closeModal() {
    this.showConfirmModal = false;
  }

  skipStep() {
    this.showConfirmModal = false;
    // Check if all subscribers have no trips assigned
    const allWithoutTrips = this.subscribers.every(sub => !sub?.trips || sub?.trips?.length === 0);
    if (allWithoutTrips) {
      this.router.navigateByUrl('/buses/students');
    } else {
      this.saveAssignments();
    }
  }

  assignTripsNow() {
    this.showConfirmModal = false;
    // Do nothing, let the user assign trips
  }
  saveAssignments() {
    this.allowNavigation = true;
    this.loading = true;
    const payload = {
      trips: this.trips
        .filter(trip => trip.subscribers && trip.subscribers.length > 0)
        .map(trip => ({
          id: Number(trip.trip_id),
          subscribers: trip.subscribers
        }))
    };
    this.busBulkEnrollService.assignTrips(payload).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (res?.success) {
        this.loading = false;
        this.router.navigateByUrl('/buses/students');
      }
      else if (!res?.success && res?.error) {
        this.loading = false;
        this.errorMsgService.setMessage(res?.error);
        this.allowNavigation = false;
      }
    },
      (err: any) => {
        this.loading = false;
        this.errorMsgService.setMessage(err?.error?.message);
        this.allowNavigation = false;
      });
  }

  leavePage() {
    this.showNavigationModal = false;
    this.allowNavigation = true;
    if (this.resolveNavigation) {
      this.resolveNavigation(true);
    } else {
      this.router.navigateByUrl(this.navigationRoute);
    }
  }

  stayOnPage() {
    this.showNavigationModal = false;
    if (this.resolveNavigation) {
      this.resolveNavigation(false);
    }
  }

  // Call this method when user tries to navigate away manually
  promptNavigationModal() {
    this.showNavigationModal = true;
  }
}


